/** A link to a Notion page, named from its address. */
export interface NotionPage {
  url: string
  /** The page's name as its address spells it, or null when the address carries none. */
  title: string | null
}

const PAGE_ID = /(?:^|-)([0-9a-f]{32}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
// Notion turns an apostrophe in a title into a hyphen: "Mum's birthday" becomes "Mum-s-birthday"
const CONTRACTION = new Set(['s', 't', 'd', 'm', 'll', 're', 've'])

function isNotionHost(host: string): boolean {
  return ['notion.so', 'notion.site', 'notion.com'].some((h) => host === h || host.endsWith(`.${h}`))
}

/**
 * A page address names the page in its last part, before the page id:
 * notion.so/chris/Plan-Mum-s-birthday-1a2b…, or chris.notion.site/Plan-Mum-s-birthday-1a2b….
 * Only addresses that end in a page id count, so notion.so/pricing stays an ordinary link.
 * The name comes from the address alone: reading the page itself would need a Notion connection.
 */
export function notionPage(url: string): NotionPage | null {
  let parsed: URL
  try {
    parsed = new URL(url)
  } catch {
    return null
  }
  if (!/^https?:$/.test(parsed.protocol) || !isNotionHost(parsed.hostname.toLowerCase())) return null
  const last = parsed.pathname.split('/').filter(Boolean).pop() ?? ''
  const id = PAGE_ID.exec(last)
  if (!id) return null
  // a page peeked open over a database: the address names the database, not the page
  if (parsed.searchParams.has('p')) return { url, title: null }
  return { url, title: titleFromSlug(last.slice(0, id.index)) }
}

function titleFromSlug(slug: string): string | null {
  let text = slug
  try {
    text = decodeURIComponent(slug)
  } catch {
    // a stray % in a hand-typed address: keep the slug as it is
  }
  const words = text.split('-').filter(Boolean)
  if (words.length === 0) return null
  return words.reduce((out, w, i) => (i > 0 && CONTRACTION.has(w) ? `${out}'${w}` : i > 0 ? `${out} ${w}` : w), '')
}
